package com.swetdot.swetgram.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import com.swetdot.swetgram.ui.design.ChatColors
import com.swetdot.swetgram.ui.design.ProvideSwetMotion
import com.swetdot.swetgram.ui.design.SwetgramColors
import com.swetdot.swetgram.ui.design.SwetgramShapes
import com.swetdot.swetgram.ui.design.platformReduceMotion

/**
 * The Swetgram theme.
 *
 * The palette is no longer authored here. It lives in [SwetgramColors] as the Design System's
 * own tokens, and the Material3 `ColorScheme` is derived from those rather than written by
 * hand -- the previous scheme was a teal of its own invention, which matched nothing in the
 * SDS. Screens read tokens through [SwetgramThemeTokens]; Material's own components keep
 * working because the derived scheme is still installed.
 */

val LocalSwetgramColors = staticCompositionLocalOf { SwetgramColors.Dark }
val LocalChatColors = staticCompositionLocalOf { ChatColors.from(SwetgramColors.Dark) }

/**
 * THERE IS ONE THEME AND IT IS DARK. `isSystemInDarkTheme()` is not consulted.
 *
 * The user's decision, and it is a design decision rather than a default: the app is black with
 * white text, whatever the system setting says. Every mockup this app was built to is dark, and a
 * light palette in a strict-monochrome design is not "the same design in reverse" -- it is a second
 * design, with its own contrast arithmetic, its own avatar surfaces and its own near-white bubble
 * problem, maintained by nobody.
 *
 * THE PARAMETER IS GONE RATHER THAN IGNORED. A `darkTheme: Boolean = isSystemInDarkTheme()` left in
 * place and no longer read is precisely this project's oldest defect -- a switch that looks live and
 * is not. A caller passing `false` would get dark and never learn why.
 */
@Composable
fun SwetgramTheme(content: @Composable () -> Unit) {
    val colors = SwetgramColors.Dark
    val reduceMotion = platformReduceMotion()

    CompositionLocalProvider(
        LocalSwetgramColors provides colors,
        LocalChatColors provides ChatColors.from(colors),
    ) {
        ProvideSwetMotion(reduceMotion = reduceMotion) {
            MaterialTheme(
                colorScheme = colors.toColorScheme(),
                typography = SwetgramTypography,
                shapes = SwetgramShapes,
            ) {
                // MaterialTheme only makes the colours AVAILABLE; nothing paints them. Without a
                // Surface at the root the window background shows through from the platform theme
                // -- on a MIUI device that is the system accent, so the app rendered pink despite
                // every token being correct. Only visible on a real device: previews and tests both
                // draw on their own background.
                Surface(
                    modifier = Modifier.fillMaxSize(),
                    color = MaterialTheme.colorScheme.background,
                    contentColor = MaterialTheme.colorScheme.onBackground,
                    content = content,
                )
            }
        }
    }
}

/**
 * How a screen reaches the tokens Material3 has no slot for.
 *
 * Anything a screen needs that is not a Material colour role -- `textTertiary`, `borderStrong`,
 * `success`, the chat bubble roles -- comes from here. A screen should never write a literal
 * `Color(0x...)`: that is how the app ended up with three different accents.
 */
object SwetgramThemeTokens {
    val colors: SwetgramColors
        @Composable @ReadOnlyComposable get() = LocalSwetgramColors.current

    val chat: ChatColors
        @Composable @ReadOnlyComposable get() = LocalChatColors.current
}
